import { useState, useEffect, useCallback } from 'react';
import {
  qualitySettingsService,
  type PersistedQualitySettings,
} from '@/shared/services/qualitySettingsService';
import {
  SCAN_QUALITY_THRESHOLDS,
  QUALITY_GATE_TIMEOUT_MS,
  DEFAULT_LIVENESS_MODE,
  type QualityThresholds,
  type LivenessMode,
} from '@/shared/config/quality.config';
import { FACE_MATCH_THRESHOLD_COSINE } from '@/shared/config/terminal.config';

export const DEFAULT_QUALITY_SETTINGS: PersistedQualitySettings = {
  thresholds: SCAN_QUALITY_THRESHOLDS,
  livenessMode: DEFAULT_LIVENESS_MODE,
  qualityGateTimeoutMs: QUALITY_GATE_TIMEOUT_MS,
  matchThreshold: FACE_MATCH_THRESHOLD_COSINE,
  // NEW Slice 3: Liveness Aggregator defaults
  passiveHigh: 0.85,
  passiveLow: 0.40,
  livenessWindowSize: 5,
  livenessLiveCount: 3,
  livenessSpoofConsecutive: 3,
  livenessMaxTotalFrames: 30,
  // NEW Slice 3: Gate B override defaults
  blurMinLaplacianVar: SCAN_QUALITY_THRESHOLDS.blurThreshold,
  brightnessMinGateB: SCAN_QUALITY_THRESHOLDS.brightnessMin,
  brightnessMaxGateB: SCAN_QUALITY_THRESHOLDS.brightnessMax,
  faceMinShortSidePx: SCAN_QUALITY_THRESHOLDS.faceMinPx,
  // Modo home default → Automático (single blue button). Aligned with the
  // Zod schema default so first-boot terminals get the same UX as terminals
  // that have never opened the Calidad y Liveness modal.
  homeButtonAuto: true,
  // Consecutive timelog validation — minimum seconds between same accessType (offline only)
  consecutiveTimelogMinSeconds: 90,
  // Quality preset (default or high-contrast for bright environments)
  qualityPreset: 'default' as const,
  // Frame throttle FPS — default 5 FPS
  frameThrottleFps: 5,
};

/**
 * HIGH_CONTRAST_QUALITY_PRESET
 * Preset optimizado para cámaras de baja calidad (celulares gama baja/media).
 * 
 * OBJETIVO: Que Juan con cámara mala pueda ser reconocido como Juan.
 * NO OBJETIVO: Que María sea reconocida como Juan.
 * 
 * Cambios vs default:
 * - MÁS PERMISIVO en calidad de imagen: blur (40), brillo (70-210), tamaño (95)
 * - MÁS TIEMPO: timeout aumentado a 12s (crítico para celulares lentos)
 * - MÁS TOLERANTE en liveness: scores erráticos de cámara mala
 * - MATCHING SIN CAMBIOS: mantiene threshold 0.6 (no compromete seguridad)
 */
export const HIGH_CONTRAST_QUALITY_PRESET: Partial<PersistedQualitySettings> = {
  thresholds: {
    ...SCAN_QUALITY_THRESHOLDS,
    blurThreshold: 40,        // default: 50 → acepta blur normal de celular (no afecta matching)
    brightnessMin: 70,        // default: 80 → acepta rango más amplio (exposición auto mala)
    brightnessMax: 210,       // default: 200 → acepta rango más amplio
    faceMinPx: 95,            // default: 110 → acepta rostro ligeramente más pequeño
  },
  matchThreshold: 0.6,        // default: 0.6 → SIN CAMBIOS (mantiene precisión de matching)
  passiveHigh: 0.50,          // default: 0.85 → MÍNIMO permitido por schema (acepta scores 0.50+)
  passiveLow: 0.01,           // default: 0.40 → MÍNIMO ABSOLUTO (acepta score 0.056 del log)
  livenessWindowSize: 3,      // default: 5 → ventana MÁS chica (decide más rápido con scores borderline)
  livenessLiveCount: 2,       // default: 3 → menos frames "live" requeridos (scores erráticos)
  livenessSpoofConsecutive: 8, // default: 3 → MUCHOS frames "spoof" para rechazar (evita falsos negativos)
  livenessMaxTotalFrames: 80, // default: 30 → MUCHOS intentos antes de timeout (perfil lateral muy lento)
  blurMinLaplacianVar: 40,    // coherente con blurThreshold
  brightnessMinGateB: 70,     // coherente con brightnessMin
  brightnessMaxGateB: 210,    // coherente con brightnessMax
  faceMinShortSidePx: 95,     // coherente con faceMinPx
  qualityPreset: 'high-contrast' as const,
  frameThrottleFps: 2,        // default: 5 → 2 FPS para celulares de muy bajos recursos (reduce carga CPU)
};

export interface UseQualitySettingsResult {
  thresholds: QualityThresholds;
  livenessMode: LivenessMode;
  qualityGateTimeoutMs: number;
  matchThreshold: number;
  // NEW Slice 3: Liveness Aggregator fields
  passiveHigh: number;
  passiveLow: number;
  livenessWindowSize: number;
  livenessLiveCount: number;
  livenessSpoofConsecutive: number;
  livenessMaxTotalFrames: number;
  // NEW Slice 3: Gate B override fields
  blurMinLaplacianVar: number;
  brightnessMinGateB: number;
  brightnessMaxGateB: number;
  faceMinShortSidePx: number;
  homeButtonAuto: boolean;
  consecutiveTimelogMinSeconds: number;
  qualityPreset: 'default' | 'high-contrast';
  frameThrottleFps: number;
  isLoaded: boolean;
  /**
   * Persist quality settings. Merges `partial` with the current settings and
   * validates via `PersistedQualitySettingsSchema.parse` before writing to
   * IndexedDB.
   *
   * @throws {ZodError} When the merged settings fail schema validation
   *   (min/max ranges or active cross-field refines: `livenessLiveCount ≤
   *   livenessWindowSize`, `livenessSpoofConsecutive ≤ livenessWindowSize`).
   * @throws {Error} On unexpected persistence failure (e.g. Dexie errors).
   *
   * The hook does NOT catch either. Callers are responsible for surfacing
   * errors to the UI; see `AdminConfigPage.handleQsSave` for the reference
   * try/catch/finally pattern.
   */
  save: (partial: Partial<PersistedQualitySettings>) => Promise<void>;
  reset: () => Promise<void>;
}

/**
 * Applies a quality preset by merging preset values with current settings.
 * Only affects image quality, face matching, and fraud detection params.
 * Does NOT affect: livenessMode, homeButtonAuto, or other UI toggles.
 */
export function applyQualityPreset(
  preset: 'default' | 'high-contrast',
  currentSettings: PersistedQualitySettings,
): PersistedQualitySettings {
  const presetValues = preset === 'default' 
    ? DEFAULT_QUALITY_SETTINGS 
    : { ...DEFAULT_QUALITY_SETTINGS, ...HIGH_CONTRAST_QUALITY_PRESET };

  return {
    ...currentSettings,
    // Apply preset values (image quality, matching, fraud detection)
    thresholds: presetValues.thresholds,
    matchThreshold: presetValues.matchThreshold,
    passiveHigh: presetValues.passiveHigh,
    passiveLow: presetValues.passiveLow,
    blurMinLaplacianVar: presetValues.blurMinLaplacianVar,
    brightnessMinGateB: presetValues.brightnessMinGateB,
    brightnessMaxGateB: presetValues.brightnessMaxGateB,
    faceMinShortSidePx: presetValues.faceMinShortSidePx,
    frameThrottleFps: presetValues.frameThrottleFps,
    qualityPreset: preset,
    // Keep existing values for: livenessMode, homeButtonAuto, qualityGateTimeoutMs, etc.
  };
}

export function useQualitySettings(): UseQualitySettingsResult {
  const [settings, setSettings] = useState<PersistedQualitySettings>(DEFAULT_QUALITY_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    void qualitySettingsService.load().then((loaded) => {
      if (loaded !== null) {
        setSettings(loaded);
      }
      setIsLoaded(true);
    });
  }, []);

  const save = useCallback(async (partial: Partial<PersistedQualitySettings>) => {
    const merged: PersistedQualitySettings = { ...settings, ...partial };
    await qualitySettingsService.save(merged);
    setSettings(merged);
  }, [settings]);

  const reset = useCallback(async () => {
    await qualitySettingsService.reset();
    setSettings(DEFAULT_QUALITY_SETTINGS);
  }, []);

  return {
    thresholds: settings.thresholds,
    livenessMode: settings.livenessMode,
    qualityGateTimeoutMs: settings.qualityGateTimeoutMs,
    matchThreshold: settings.matchThreshold,
    // NEW Slice 3: Liveness Aggregator fields
    passiveHigh: settings.passiveHigh,
    passiveLow: settings.passiveLow,
    livenessWindowSize: settings.livenessWindowSize,
    livenessLiveCount: settings.livenessLiveCount,
    livenessSpoofConsecutive: settings.livenessSpoofConsecutive,
    livenessMaxTotalFrames: settings.livenessMaxTotalFrames,
    // NEW Slice 3: Gate B override fields
    blurMinLaplacianVar: settings.blurMinLaplacianVar,
    brightnessMinGateB: settings.brightnessMinGateB,
    brightnessMaxGateB: settings.brightnessMaxGateB,
    faceMinShortSidePx: settings.faceMinShortSidePx,
    homeButtonAuto: settings.homeButtonAuto,
    consecutiveTimelogMinSeconds: settings.consecutiveTimelogMinSeconds,
    qualityPreset: settings.qualityPreset,
    frameThrottleFps: settings.frameThrottleFps,
    isLoaded,
    save,
    reset,
  };
}
